import { useState } from "react";
import { useFormWithValidation } from "@/features/forms/hooks/useFormWithValidation";
import { FormProvider } from "react-hook-form";

import FormInput from "@/features/forms/components/FormInput";
import InputPassword from "@/features/forms/components/InputPassword";
import InputPhone from "@/features/forms/components/InputPhone";
import Spinner from "@/features/forms/components/Spinner";
import Checkbox from "@/features/forms/components/Checkbox";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import Button from "@/ui/button/Button";
import Icon from "@/ui/icon/Icon";
import { errorToast } from "@/ui/toast/Toast";
import i18n from "@/i18n";
import { fieldErrorText } from "@/features/forms/fieldErrorText";

import style from "./RegisterDetailsStep.module.css";

import { useRegister } from "../../hooks/useRegister";

// The link text sits in a span (the stylesheet underlines it), so the span
// lives here and the catalogue only has to say <terms>…</terms>.
function TermsLink({ to, children }) {
	return (
		<Link to={to}>
			<span>{children}</span>
		</Link>
	);
}

function RegisterDetailsStep({ email, code, prevStep, nextStep, emailStep }) {
	const { t } = useTranslation();
	const [terms, setTerms] = useState(false);

	const formMethods = useFormWithValidation({
		defaultValues: {
			username: email && email.split("@")[0],
		},
	});

	const {
		handleSubmit,
		formState: { isSubmitting },
	} = formMethods;

	const { register } = useRegister();

	const onSubmit = async function (data) {
		try {
			await register({
				email: email,
				code: code,
				...data,
			});
		} catch (e) {
			if (e?.response?.data?.errors?.email?.length) {
				const fieldError = e.response.data.errors.email[0];
				errorToast(
					fieldErrorText(
						{ ...fieldError, code: fieldError?.code ?? fieldError?.error_code },
						i18n.resolvedLanguage,
						i18n
					)
				);
				emailStep();
			} else if (e?.response?.data?.errors?.code?.length) {
				const fieldError = e.response.data.errors.code[0];
				errorToast(
					fieldErrorText(
						{ ...fieldError, code: fieldError?.code ?? fieldError?.error_code },
						i18n.resolvedLanguage,
						i18n
					)
				);
				prevStep();
			}
			throw e;
		}

		nextStep();
	};

	return (
		<div className={style.content}>
			<div className={style.header}>
				<Icon icon="fa-user" />
				<h2>{t("auth:register.personalInfo")}</h2>
			</div>
			<FormProvider {...formMethods}>
				<form className={style.form} onSubmit={handleSubmit(onSubmit)}>
					<FormInput
						name="username"
						autoComplete="username"
						label={t("common:fields.username.label")}
						placeholder={t("common:fields.username.placeholder")}
					/>
					<FormInput
						name="first_name"
						label={t("common:fields.firstName.label")}
						placeholder={t("common:fields.firstName.placeholder")}
					/>
					<FormInput
						name="last_name"
						label={t("common:fields.lastName.label")}
						placeholder={t("common:fields.lastName.placeholder")}
					/>
					<InputPhone
						name="phone_number"
						label={t("common:fields.phone.label")}
						placeholder={t("common:fields.phone.placeholder")}
					/>
					<div className={style.passwordHolder}>
						<InputPassword
							name="password1"
							autoComplete="new-password"
							label={t("common:fields.password.label")}
							placeholder={t(
								"common:fields.password.placeholder"
							)}
						/>
						<InputPassword
							name="password2"
							label={t("common:fields.confirmPassword.label")}
							placeholder={t(
								"auth:register.details.confirmPasswordPlaceholder"
							)}
						/>
					</div>
					<div className={style.termsOfUse}>
						<Checkbox
							id="terms"
							checked={terms}
							onCheckedChange={(val) => setTerms(val)}
						/>
						<label htmlFor="terms">
							<Trans
								i18nKey="auth:register.details.terms"
								components={{
									terms: (
										<TermsLink to="/terms-and-conditions" />
									),
									privacy: <TermsLink to="/privacy-policy" />,
								}}
							/>
						</label>
					</div>
					<div className={style.buttonHolder}>
						<Button disabled={!terms || isSubmitting} type="accent">
							{isSubmitting ? (
								<Spinner />
							) : (
								t("common:actions.continue")
							)}
						</Button>
					</div>
				</form>
			</FormProvider>
		</div>
	);
}

export default RegisterDetailsStep;
